/**
 * TCPN PHASE S1-C2 — Public Logo Badge
 * =====================================
 * Compact, marketplace-grade logo presenter. Renders the canonical approved
 * logo when present, otherwise a deterministic initials avatar so layout
 * stays stable. Pure presentation — never fetches, never mutates state.
 *
 * Sizes are intentionally constrained to 32-64 px to preserve the dense
 * card scan style of the search surface (medical-marketplace seriousness).
 */
import React, { useMemo } from 'react';

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function _initials(name) {
  if (!name) return '·';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Deterministic neutral palette — avoids loud colours; preserves the
// healthcare-professional tone. Hue is derived from the name hash so the
// fallback stays stable across renders and surfaces.
const PALETTE = [
  ['bg-slate-100', 'text-slate-600'],
  ['bg-teal-50', 'text-teal-700'],
  ['bg-sky-50', 'text-sky-700'],
  ['bg-emerald-50', 'text-emerald-700'],
  ['bg-indigo-50', 'text-indigo-700'],
  ['bg-amber-50', 'text-amber-800'],
];

function _palette(name) {
  if (!name) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

/**
 * Resolve a logo asset into an absolute URL the browser can fetch
 * anonymously. The backend exposes only `/api/media/{id}?variant=...`
 * which is publicly readable iff `is_published=true AND moderation_status=approved`.
 *
 * TREATX-SEO-RECOVERY-S3B — exported because the canonical public provider
 * payload carries `logo` as a MEDIA DESCRIPTOR object
 * (`{media_id, thumbnail_url, preview_url}` | null), not a URL string. Any
 * consumer that needs a URL — an <img>, an og:image, a JSON-LD `logo` — must
 * resolve it HERE rather than re-deriving the shape, which is exactly the
 * mistake that blanked the provider page.
 */
export function resolveClinicLogoUrl(logo, variant = 'thumbnail') {
  if (!logo) return null;
  // Prefer pre-built URL pointers from backend payload
  const base = (REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  const path = variant === 'preview' ? logo.preview_url : logo.thumbnail_url;
  if (path) return `${base}${path}`;
  if (logo.media_id) return `${base}/api/media/${logo.media_id}?variant=${variant}`;
  return null;
}

/**
 * <ClinicLogoBadge>
 *   logo:   { media_id, thumbnail_url, preview_url } | null | undefined
 *   name:   clinic display name (used for initials fallback + alt text)
 *   size:   'xs' | 'sm' | 'md' (32 | 40 | 56 px)
 *   testid: optional data-testid prefix (default 'clinic-logo')
 */
export default function ClinicLogoBadge({ logo, name, size = 'sm', testid = 'clinic-logo' }) {
  const dim = size === 'xs' ? 'w-8 h-8 text-[10px]'
            : size === 'md' ? 'w-14 h-14 text-base'
            : 'w-10 h-10 text-xs';
  const url = resolveClinicLogoUrl(logo, size === 'md' ? 'preview' : 'thumbnail');
  const present = Boolean(url);
  const initials = useMemo(() => _initials(name), [name]);
  const [bg, fg] = useMemo(() => _palette(name), [name]);

  if (present) {
    return (
      <div
        className={`${dim} flex-shrink-0 rounded-md bg-white border border-slate-200 overflow-hidden flex items-center justify-center`}
        data-testid={`${testid}-image`}
        data-logo-state="present"
      >
        <img
          src={url}
          alt={name ? `${name} logo` : 'clinic logo'}
          className="max-w-full max-h-full object-contain"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      </div>
    );
  }
  return (
    <div
      className={`${dim} flex-shrink-0 rounded-md ${bg} ${fg} border border-slate-200 flex items-center justify-center font-semibold tracking-tight`}
      data-testid={`${testid}-fallback`}
      data-logo-state="fallback"
      aria-label={name ? `${name} logo placeholder` : 'clinic logo placeholder'}
    >
      {initials}
    </div>
  );
}
